/**
 * Docfiller types — aliased from the generated `schema.d.ts` so consumers
 * stay in sync with the backend contract.
 */

import type { components } from './schema'

export type DocumentStatus = components['schemas']['DocumentStatus']
export type FieldKind = components['schemas']['FieldKind']
export type AcceptedBy = components['schemas']['AcceptedBy']
export type DocumentErrorCode = components['schemas']['DocumentErrorCode']
export type ProgressStage = components['schemas']['ProgressStage']

export type DocumentSummary = components['schemas']['DocumentRead']
export type Field = components['schemas']['FieldRead']
export type DocumentDetail = components['schemas']['DocumentDetail']
export type DocumentUpdateBody = components['schemas']['DocumentUpdate']
export type FieldCreateBody = components['schemas']['FieldCreate']
export type FieldDefinitionPatchBody =
  components['schemas']['FieldDefinitionPatch']

// ``force_overwrite`` defaults to ``false`` server-side, but openapi-typescript
// v7 emits fields with a ``default`` as required. Loosen the type on the
// client so the common save-without-force path doesn't have to pass it.
export type PatchFieldsBody = Omit<
  components['schemas']['BulkPatchFields'],
  'force_overwrite'
> & {
  force_overwrite?: boolean | null
}
export type FillRequestBody = components['schemas']['FillRequest']
export type FillResponseBody = components['schemas']['FillResponse']
export type TemplateUpdateBody = components['schemas']['TemplateUpdateRequest']
export type TemplateQuickFillBody =
  components['schemas']['TemplateQuickFillRequest']
export type TemplateQuickFillResponse =
  components['schemas']['TemplateQuickFillResponse']

export type OutputFormat = components['schemas']['OutputFormat']
export type RenderRequestBody = components['schemas']['RenderRequest']
export type RenderResponseBody = components['schemas']['RenderResponse']
export type DocumentOutput = components['schemas']['DocumentOutputRead']
export type FieldEdit = components['schemas']['FieldEditRead']
export type EditActor = components['schemas']['EditActor']

export type AiEditAction = components['schemas']['AiEditAction']
export type AiEditScope = components['schemas']['AiEditScope']
export type SuggestEditsBody = components['schemas']['SuggestEditsRequest']
export type SuggestEditsResponseBody =
  components['schemas']['SuggestEditsResponse']
export type SuggestEditProposal =
  components['schemas']['SuggestEditProposal']
export type SuggestEditRejection =
  components['schemas']['SuggestEditRejection']

export type WorkspaceContextRead = components['schemas']['WorkspaceContextRead']
export type WorkspaceContextWrite = components['schemas']['WorkspaceContextWrite']
export type DocumentContextUpdate =
  components['schemas']['DocumentContextUpdate']
export type ContextScope = components['schemas']['ContextScope']
export type ContextAuditAction = components['schemas']['ContextAuditAction']
export type WorkspaceContextAuditEvent =
  components['schemas']['WorkspaceContextAuditEventRead']

// Mirrors backend caps so the UI can communicate token budgets without
// round-tripping a probe call. Keep these in sync with
// ``MAX_CONTEXT_CHARS`` in
// ``backend/app/routers/dashboard/workspace_context.py`` and
// ``COMPOSED_CONTEXT_BUDGET_CHARS`` in
// ``backend/app/services/workspace_context.py``.
export const MAX_CONTEXT_CHARS = 16_000
export const COMPOSED_CONTEXT_BUDGET_CHARS = 24_000

// Per-section header strings the backend prepends inside ``compose_fill_context``.
// We render the same labels in the precedence panel so what the user sees
// before submit matches what the backend will assemble.
export const CONTEXT_SECTION_LABELS = {
  snapshot: 'Document snapshot (per-doc override)',
  document: 'Document reusable context',
  user: 'Workspace default context',
} as const

export interface ContextSliceSummary {
  /** Stable id for React keys + selection state. */
  key: 'snapshot' | 'document' | 'user'
  label: string
  description: string
  /** Trimmed content. Empty string when the slice is unset. */
  content: string
  /** True if this slice will contribute to the composed prompt. */
  active: boolean
  /** Approximate char count after trimming + section header overhead. */
  chars: number
}

export type JobRead = components['schemas']['JobRead']
export type JobStatus = components['schemas']['JobStatus']

// ── Anchored comments + field-change suggestions (review surface) ─────
export type CommentAnchor = components['schemas']['CommentAnchor']
export type CommentAnchorType = components['schemas']['CommentAnchorType']
export type DocumentComment = components['schemas']['DocumentCommentRead']
export type DocumentCommentCreate =
  components['schemas']['DocumentCommentCreate']
export type DocumentCommentUpdate =
  components['schemas']['DocumentCommentUpdate']
export type DocumentSuggestion =
  components['schemas']['DocumentSuggestionRead']
export type DocumentSuggestionCreate =
  components['schemas']['DocumentSuggestionCreate']
export type SuggestionStatus = components['schemas']['SuggestionStatus']
export type SuggestionKind = components['schemas']['SuggestionKind']

// ── Saved prompts (workspace-shared fill-instruction library) ─────────
export type SavedPromptRead = components['schemas']['SavedPromptRead']
export type SavedPromptCreate = components['schemas']['SavedPromptCreate']
export type SavedPromptUpdate = components['schemas']['SavedPromptUpdate']

// ── Effective instructions (precedence preview) ────────────────────────
export type InstructionScope = components['schemas']['InstructionScope']
export type InstructionSegment = components['schemas']['InstructionSegmentRead']
export type EffectiveInstructions =
  components['schemas']['EffectiveInstructionsRead']

// Mirrors the backend's shared per-block size cap (``MAX_CONTEXT_CHARS``
// in ``app.routers.dashboard.workspace_context``). The saved-prompt route
// uses the same bound, so keep this in sync if the server raises or
// lowers it.
export const MAX_SAVED_PROMPT_CHARS = 16_000
export const MAX_SAVED_PROMPT_TITLE_CHARS = 200

/**
 * Rough char → token estimate matching
 * ``app.services.instructions.estimate_tokens`` (4 chars / token). Kept in
 * sync with the backend so the precedence preview shows the same advisory
 * budget the API exposes.
 */
export function estimateTokensFromChars(chars: number): number {
  if (chars <= 0) return 0
  return Math.ceil(chars / 4)
}

export type ChatRole = components['schemas']['ChatRole']
export type ChatMessageKind = components['schemas']['ChatMessageKind']
export type ChatSessionSummary = components['schemas']['ChatSessionSummary']
export type ChatSessionDetail = components['schemas']['ChatSessionDetail']
export type ChatMessageRead = components['schemas']['ChatMessageRead']
export type ChatMessageResponse = components['schemas']['ChatMessageResponse']
export type ChatContextSummary = components['schemas']['ChatContextSummary']

export type EditProposalRead = components['schemas']['EditProposalRead']
export type EditProposalStatus = components['schemas']['EditProposalStatus']

// ───────────────────────── Document revisions (PR #130) ─────────────────

export type RevisionActor = components['schemas']['RevisionActor']
export type RevisionSummary = components['schemas']['RevisionSummary']
export type RevisionDetail = components['schemas']['RevisionDetail']
export type RevisionFieldChange = components['schemas']['RevisionFieldChange']
export type FieldConflict = components['schemas']['FieldConflict']
export type RevisionConflict = components['schemas']['RevisionConflict']

// ───────────────────────── Realtime collaboration ────────────────────────

export type FieldConflictPayload =
  components['schemas']['FieldConflictPayload']
export type FieldConflictDetail =
  components['schemas']['FieldConflictDetail']

export type AiActionRead = components['schemas']['AiActionRead']
export type AiActionCreate = components['schemas']['AiActionCreate']
export type AiActionStatus = components['schemas']['AiActionStatus']
export type AiActionType = components['schemas']['AiActionType']
export type AiFieldEditProposal = components['schemas']['AiFieldEditProposal']

/**
 * Shape of ``AiActionRead.payload`` for a single field edit. The wire schema
 * types ``payload`` as ``{[k: string]: unknown}`` because Pydantic doesn't
 * have a discriminator there; this local type narrows it for the UI without
 * touching the generated schema.
 */
export interface AiFieldEditPayloadEntry {
  field_uuid: string
  before?: string | null
  after?: string | null
  friendly_name?: string | null
  kind?: string | null
  expected_updated_at?: string | null
}

// `clear_suggested` is required in the schema (defaults to false on the
// server). Make it optional on the client so call sites that only touch
// `current_value` or `accepted_by` don't have to thread the flag through.
export type PatchSingleFieldBody = Partial<components['schemas']['FieldPatch']>

export type PreviewMode = components['schemas']['PreviewMode']
export type PreviewRequestBody = components['schemas']['PreviewRequest']


export type SSEEventName = 'field_proposal' | 'message' | 'done' | 'error'

export interface FieldProposalEvent {
  field_name: string
  friendly_name?: string | null
  field_kind?: string | null
  value: string
  confidence: number | null
  reasoning: string | null
  field_uuid: string | null
  run_id: string
  /**
   * Live SSE runs always emit ``false`` — the relay writes proposals to
   * ``suggested_value`` only, and acceptance must go through the
   * auth-protected ``ai-actions/apply`` or field PATCH endpoints. The
   * flag stays in the wire payload for backwards compatibility with
   * historic transcripts that were persisted while auto-apply was still
   * the policy; render bubbles accordingly.
   */
  applied: boolean
}

export interface ChatMessageEvent {
  role: 'assistant' | 'user'
  content: string
}

export interface DoneEvent {
  run_id: string
}

export interface ErrorEvent {
  message: string
}

export const MIME_PDF = 'application/pdf'
// Legacy alias still emitted by some browsers / upload clients. The backend
// normalises this to ``application/pdf`` in ``validate_upload``; we accept
// it here so the client preflight doesn't bounce a file the server would
// otherwise take.
export const MIME_PDF_ALIAS = 'application/x-pdf'
export const MIME_DOCX =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024
export const MAX_UPLOAD_FILES = 1

/** Accepted file extensions for the document upload path. Lower-cased. */
export const UPLOAD_ACCEPTED_EXTENSIONS = ['.pdf', '.docx'] as const

/** Accepted MIME types — mirrors the backend's `validate_upload`. */
export const UPLOAD_ACCEPTED_MIMES = [
  MIME_PDF,
  MIME_PDF_ALIAS,
  MIME_DOCX,
] as const

/**
 * Pre-flight validation for one `File` before we POST. Mirrors the
 * server-side `validate_upload` — the server is still the source of
 * truth (browsers lie about `file.type`), this is just a UX shortcut so
 * obvious errors don't round-trip.
 *
 * Returns a user-facing message on failure (suitable for a toast) or
 * `null` when the file looks acceptable.
 */
export function validateUploadFile(file: File): string | null {
  if (file.size === 0) return 'The file is empty.'
  if (file.size > MAX_UPLOAD_BYTES) {
    const limit = `${Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024))}MB`
    return `File is too large. The limit is ${limit}.`
  }
  const lower = file.name.toLowerCase()
  const ext = lower.includes('.')
    ? '.' + lower.slice(lower.lastIndexOf('.') + 1)
    : ''
  const okExt =
    ext === '' ||
    UPLOAD_ACCEPTED_EXTENSIONS.some((e) => e === ext)
  const okMime =
    file.type === '' ||
    UPLOAD_ACCEPTED_MIMES.some((m) => m === file.type)
  if (!okExt) return 'Only PDF and Word (.docx) documents are supported.'
  if (!okMime) return 'Only PDF and Word (.docx) documents are supported.'
  // When BOTH ext AND mime are present they must agree — catches an
  // ``evil.pdf`` masquerading as a DOCX content-type (or vice versa)
  // before the server has to reject it.
  if (ext && file.type) {
    const isPdfMime =
      file.type === MIME_PDF || file.type === MIME_PDF_ALIAS
    if (ext === '.pdf' && !isPdfMime) {
      return 'The file extension does not match the file type.'
    }
    if (ext === '.docx' && file.type !== MIME_DOCX) {
      return 'The file extension does not match the file type.'
    }
  }
  return null
}

/**
 * Validate a *batch* of files at once. Used by drag/drop handlers and
 * file pickers that may surface several files in a single user action.
 * Returns the first error encountered along with the index, so the
 * caller can decide whether to surface a per-file message or a generic
 * "drop one file at a time" banner.
 */
export function validateUploadBatch(
  files: readonly File[],
): { ok: true } | { ok: false; message: string } {
  if (files.length === 0) {
    return { ok: false, message: 'No file selected.' }
  }
  if (files.length > MAX_UPLOAD_FILES) {
    return {
      ok: false,
      message: `Drop one file at a time — up to ${MAX_UPLOAD_FILES} per upload.`,
    }
  }
  for (const f of files) {
    const err = validateUploadFile(f)
    if (err) return { ok: false, message: err }
  }
  return { ok: true }
}

/**
 * Parse a server error body returned by `POST /dashboard/documents`
 * and return the user-facing message. FastAPI normally returns
 * `{ detail: "..." }`; this helper falls back to the raw text when the
 * body isn't JSON so a 502 from a proxy still produces *some* message.
 */
export function parseUploadErrorBody(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) return detail.trim()
  }
  if (typeof body === 'string' && body.trim()) return body.trim()
  return `Upload failed (HTTP ${status}). Please try again.`
}

/**
 * Resolve the human-readable label for a form field. Falls back to the
 * raw PDF/DOCX field name when `friendly_name` is null/empty (legacy
 * docs not yet re-parsed under PR #65).
 */
export function fieldDisplayName(
  field: Pick<Field, 'name' | 'friendly_name'>,
): string {
  const friendly = field.friendly_name?.trim()
  return friendly && friendly.length > 0 ? friendly : field.name
}

// ─────────────────── Processing progress UI helpers ───────────────────────
//
// The detail page renders a stepper while the document is being processed.
// The backend's ``progress_stage`` enum is the source of truth; everything
// else here is purely presentational and lives in one place so the index
// card and detail page can't drift apart.

export interface ProgressStageStep {
  stage: ProgressStage
  label: string
  description: string
}

// Ordered top-to-bottom in the stepper. The detail page uses the array
// position to render "step X of N" + "active vs. done vs. pending" badges.
export const PROCESSING_STAGES: readonly ProgressStageStep[] = [
  {
    stage: 'queued',
    label: 'Queued',
    description: 'Waiting for a worker to pick up the document.',
  },
  {
    stage: 'downloading',
    label: 'Reading the file',
    description: 'Streaming the uploaded document into the processor.',
  },
  {
    stage: 'extracting',
    label: 'Extracting fields',
    description: 'Finding form fields and signature blocks.',
  },
  {
    stage: 'analyzing',
    label: 'Naming fields',
    description: 'Generating human-friendly labels for each field.',
  },
  {
    stage: 'ready',
    label: 'Ready to review',
    description: 'Fields extracted — open the panel to edit values.',
  },
] as const

export const AUTOFILL_STAGES: readonly ProgressStageStep[] = [
  {
    stage: 'autofilling',
    label: 'Prefilling values',
    description: 'The agent is prefilling or suggesting values from your context.',
  },
  {
    stage: 'complete',
    label: 'Done',
    description: 'Prefill finished — review the proposed values.',
  },
] as const

export const PROCESSING_STAGES_WITH_AUTOFILL: readonly ProgressStageStep[] = [
  ...PROCESSING_STAGES.slice(0, -1),
  AUTOFILL_STAGES[0]!,
  PROCESSING_STAGES[PROCESSING_STAGES.length - 1]!,
] as const

const STAGE_LABELS: Record<ProgressStage, string> = {
  queued: 'Queued',
  downloading: 'Reading the file',
  extracting: 'Extracting fields',
  analyzing: 'Naming fields',
  ready: 'Ready to review',
  autofilling: 'Prefilling values',
  complete: 'Done',
}

export function progressStageLabel(stage: ProgressStage | null | undefined): string {
  if (!stage) return 'Processing…'
  return STAGE_LABELS[stage]
}

/**
 * Map a status + error_code into a single user-facing message. Prefers the
 * backend's pre-computed ``friendly_error`` when present so the UI matches
 * REST responses (e.g. legacy rows where the backend can't classify the
 * failure still show *something* useful).
 */
export function friendlyErrorMessage(doc: {
  status: DocumentStatus
  error_code?: DocumentErrorCode | null
  error_message?: string | null
  friendly_error?: string | null
}): string {
  if (doc.friendly_error) return doc.friendly_error
  if (doc.error_message) return doc.error_message
  if (doc.status === 'error') {
    return 'Something went wrong while processing this document.'
  }
  return ''
}

/**
 * True when the document is in a "still doing work" state. The detail page
 * uses this to know whether to render the stepper at all.
 */
export function isProcessing(status: DocumentStatus): boolean {
  return status === 'uploaded' || status === 'parsing' || status === 'filling'
}

// Mirrors the backend's `canonicalize_checkbox`: an explicit off-ish
// string is unchecked, anything else non-empty is checked. PATCH writes
// always come back as "true"/"false", but legacy rows may hold PDF
// NameObject states like "/Yes" or agent suggestions like "yes" — we
// render those correctly without forcing a re-parse of every document.
const CHECKBOX_FALSY = new Set([
  'false',
  'no',
  'n',
  'off',
  '0',
  'unchecked',
  '☐', // ☐ ballot box (unchecked glyph)
])

/**
 * Truthy-cast for a checkbox field's wire value. ``null``/``""`` →
 * unchecked. Otherwise mirrors the backend rule: only an explicit
 * "off-ish" string is unchecked.
 */
export function isCheckboxChecked(
  value: string | null | undefined,
): boolean {
  if (value == null) return false
  let cleaned = value.trim()
  if (!cleaned) return false
  // PDF /V NameObjects surface as "/Yes" / "/Off" — drop the slash.
  if (cleaned.startsWith('/')) cleaned = cleaned.slice(1)
  if (!cleaned) return false
  return !CHECKBOX_FALSY.has(cleaned.toLowerCase())
}

// ─────────────────── Context precedence + token budget ───────────────────
//
// Mirrors ``compose_fill_context`` in
// ``backend/app/services/workspace_context.py``. Frontend computes the same
// active-slice + budget the backend will use so the user can see, BEFORE
// hitting Fill, exactly which slices contribute to the agent's prompt.
//
// Precedence (highest → lowest):
//   1. document snapshot (``documents.initial_context``)
//   2. document-scope reusable context
//   3. user-scope reusable workspace context (fallback for legacy uploads
//      where ``initial_context`` is null)

export interface ContextPrecedenceInputs {
  /** ``documents.initial_context`` — upload-time snapshot. */
  documentSnapshot?: string | null
  /** Document-scope ``WorkspaceContext.content``. */
  documentScope?: string | null
  /** User-scope ``WorkspaceContext.content``. */
  userScope?: string | null
}

export interface ContextPrecedenceSummary {
  /** All three slices, in highest-precedence-first order. */
  slices: ContextSliceSummary[]
  /** Slices that will actually be included in the composed prompt. */
  activeSlices: ContextSliceSummary[]
  /** Sum of active slice ``chars``, including section-header overhead. */
  composedChars: number
  /** Hard cap on the composed prompt section (see backend constant). */
  composedBudgetChars: number
  /**
   * True when the composed payload exceeds ``composedBudgetChars`` and the
   * backend will truncate the lowest-precedence slice.
   */
  composedOverBudget: boolean
  /**
   * True when the user-scope slice is shadowed by a non-empty document
   * snapshot — mirrors the backend's `user_scope` fallback being gated on
   * an empty ``initial_context``. We surface this so the UI can mark the
   * user-scope row as "inactive (snapshot wins)" instead of silently
   * dropping it from the summary.
   */
  userScopeShadowed: boolean
}

function trimOrEmpty(value: string | null | undefined): string {
  return (value ?? '').trim()
}

/**
 * Compute the precedence summary the UI uses to show the user, before
 * Fill / Generate, which slices will compose the agent's saved-context
 * section + how close to the budget they are.
 *
 * Mirrors the backend's ``compose_fill_context`` algorithm. Header chars
 * (``"## <label>\n"``) are included in each slice's char count to match
 * how the backend assembles the composed payload.
 */
export function summarizeContextPrecedence(
  inputs: ContextPrecedenceInputs,
): ContextPrecedenceSummary {
  const snapshot = trimOrEmpty(inputs.documentSnapshot)
  const docScope = trimOrEmpty(inputs.documentScope)
  const userScope = trimOrEmpty(inputs.userScope)
  const userScopeShadowed = snapshot.length > 0 && userScope.length > 0

  const sliceFor = (
    key: ContextSliceSummary['key'],
    label: string,
    description: string,
    content: string,
    active: boolean,
  ): ContextSliceSummary => {
    // Match backend's "## {label}\n{body}" formatting so the char count
    // we surface lines up with the assembled prompt section.
    const chars = content.length === 0 ? 0 : label.length + 4 + content.length
    return { key, label, description, content, active, chars }
  }

  const slices: ContextSliceSummary[] = [
    sliceFor(
      'snapshot',
      CONTEXT_SECTION_LABELS.snapshot,
      'Captured at upload — wins over both reusable contexts.',
      snapshot,
      snapshot.length > 0,
    ),
    sliceFor(
      'document',
      CONTEXT_SECTION_LABELS.document,
      'Reusable context tied to this document only.',
      docScope,
      docScope.length > 0,
    ),
    sliceFor(
      'user',
      CONTEXT_SECTION_LABELS.user,
      'Workspace-wide default — used as a fallback when no snapshot exists.',
      userScope,
      // The backend only reads the user-scope row when the snapshot is
      // empty (see ``fill_document`` in ``routers/documents.py``).
      userScope.length > 0 && snapshot.length === 0,
    ),
  ]

  const activeSlices = slices.filter((s) => s.active)
  // Two-char "\n\n" join between sections matches the backend.
  const separators = Math.max(0, activeSlices.length - 1) * 2
  const composedChars =
    activeSlices.reduce((acc, s) => acc + s.chars, 0) + separators

  return {
    slices,
    activeSlices,
    composedChars,
    composedBudgetChars: COMPOSED_CONTEXT_BUDGET_CHARS,
    composedOverBudget: composedChars > COMPOSED_CONTEXT_BUDGET_CHARS,
    userScopeShadowed,
  }
}

// ───────────────────────────── Overlay types ─────────────────────────────
//
// Renderer-independent, page-relative (0..1) coordinate model shared by
// fields, agent-proposed changes, comments, suggestions, and collaboration
// markers. The wire shape comes from the backend's ``OverlayAnchor``
// schema, versioned via ``schema_version``.

export type OverlayType = components['schemas']['OverlayType']
export type OverlayStatus = components['schemas']['OverlayStatus']
export type OverlayAuthorKind = components['schemas']['OverlayAuthorKind']
export type OverlayAnchorKind = components['schemas']['OverlayAnchorKind']
export type OverlayAnchor = components['schemas']['OverlayAnchor']
export type OverlayPoint = components['schemas']['OverlayPoint']
export type OverlayRect = components['schemas']['OverlayRect']
export type OverlayRange = components['schemas']['OverlayRange']
export type Overlay = components['schemas']['OverlayRead']
export type OverlayCreateBody = components['schemas']['OverlayCreate']
export type OverlayUpdateBody = components['schemas']['OverlayUpdate']
export type OverlayTransitionBody =
  components['schemas']['OverlayTransition']

/** Canonical anchor-schema version produced by this codebase. The renderer
 * branches on this so newer servers can introduce a v2 shape without
 * breaking older clients. */
export const OVERLAY_ANCHOR_SCHEMA_VERSION = 1

/** Per-type allow-list of status transitions. Mirrors the backend table in
 * ``app.models._ALLOWED_OVERLAY_TRANSITIONS`` exactly — kept here so the UI
 * can grey out impossible actions without round-tripping a 409. The store
 * also calls this from optimistic mutations.
 *
 * NOTE: equal source/target is always idempotent — the renderer can call
 * ``isOverlayTransitionAllowed(t, s, s)`` without a special case. */
const OVERLAY_TRANSITIONS: Readonly<
  Record<OverlayType, Readonly<Partial<Record<OverlayStatus, ReadonlySet<OverlayStatus>>>>>
> = {
  field: {
    draft: new Set<OverlayStatus>(['open', 'archived']),
    open: new Set<OverlayStatus>(['archived']),
  },
  proposed_change: {
    draft: new Set<OverlayStatus>(['open', 'archived']),
    open: new Set<OverlayStatus>(['accepted', 'rejected', 'archived']),
  },
  suggestion: {
    draft: new Set<OverlayStatus>(['open', 'archived']),
    open: new Set<OverlayStatus>(['accepted', 'rejected', 'archived']),
  },
  comment: {
    draft: new Set<OverlayStatus>(['open', 'archived']),
    open: new Set<OverlayStatus>(['resolved', 'archived']),
    resolved: new Set<OverlayStatus>(['open', 'archived']),
  },
  marker: {
    draft: new Set<OverlayStatus>(['open', 'archived']),
    open: new Set<OverlayStatus>(['archived']),
  },
}

/** Set of statuses the row could move to from ``current``. Empty when the
 * type doesn't allow any further transition. */
export function allowedOverlayTransitions(
  overlay_type: OverlayType,
  current: OverlayStatus,
): ReadonlySet<OverlayStatus> {
  const bucket = OVERLAY_TRANSITIONS[overlay_type]
  return bucket[current] ?? new Set<OverlayStatus>()
}

export function isOverlayTransitionAllowed(
  overlay_type: OverlayType,
  current: OverlayStatus,
  next: OverlayStatus,
): boolean {
  if (current === next) return true
  return allowedOverlayTransitions(overlay_type, current).has(next)
}

/** Statuses considered "terminal" — the row no longer participates in
 * further moves. UI uses this to dim accepted/rejected/archived rows. */
export function isTerminalOverlayStatus(status: OverlayStatus): boolean {
  return status === 'accepted' || status === 'rejected' || status === 'archived'
}

/** True when an anchor's coordinate-schema version matches the one this
 * client knows how to render. Renderers fall back to a "needs reload" UI
 * for unknown versions so a forward-compat server doesn't display anchors
 * in the wrong place. */
export function isOverlayAnchorSupported(anchor: OverlayAnchor): boolean {
  return anchor.schema_version === OVERLAY_ANCHOR_SCHEMA_VERSION
}

/** Best-effort accessor for the anchor's bounding-box. Returns null for
 * point/range anchors — callers that only render rectangles (e.g. the
 * fields layer) can short-circuit when the kind doesn't match. */
export function overlayBoundingRect(
  anchor: OverlayAnchor,
): { x: number; y: number; width: number; height: number } | null {
  if (anchor.kind === 'rect' && anchor.rect) {
    const { x, y, width, height } = anchor.rect
    return { x, y, width, height }
  }
  if (anchor.kind === 'range' && anchor.range) {
    const { start, end } = anchor.range
    const x = Math.min(start.x, end.x)
    const y = Math.min(start.y, end.y)
    const width = Math.abs(end.x - start.x)
    const height = Math.abs(end.y - start.y)
    return { x, y, width, height }
  }
  return null
}

/** Clamp every numeric coordinate inside the anchor into [0,1]. Used by
 * the store before sending a draft to the server so a hand-drawn
 * rectangle that briefly extended past the page edge doesn't 422. */
export function clampOverlayAnchor(anchor: OverlayAnchor): OverlayAnchor {
  const clamp = (n: number): number => Math.min(1, Math.max(0, n))
  if (anchor.kind === 'rect' && anchor.rect) {
    const x = clamp(anchor.rect.x)
    const y = clamp(anchor.rect.y)
    const maxW = 1 - x
    const maxH = 1 - y
    const width = Math.min(maxW, Math.max(Number.EPSILON, anchor.rect.width))
    const height = Math.min(maxH, Math.max(Number.EPSILON, anchor.rect.height))
    return { ...anchor, rect: { x, y, width, height } }
  }
  if (anchor.kind === 'point' && anchor.point) {
    return {
      ...anchor,
      point: { x: clamp(anchor.point.x), y: clamp(anchor.point.y) },
    }
  }
  if (anchor.kind === 'range' && anchor.range) {
    return {
      ...anchor,
      range: {
        start: { x: clamp(anchor.range.start.x), y: clamp(anchor.range.start.y) },
        end: { x: clamp(anchor.range.end.x), y: clamp(anchor.range.end.y) },
      },
    }
  }
  return anchor
}

/** Normalised view of an overlay row used by the store + renderer.
 *
 * Differs from the wire ``Overlay`` in three ways:
 *  - ``page_number`` is always pulled from ``anchor.page`` (the server
 *    enforces equality, but legacy rows from an unfinished draft may
 *    have drifted; we always trust the anchor).
 *  - ``payload`` is forced to an object so consumers don't have to
 *    branch on a possibly-null/undefined value.
 *  - ``boundingRect`` is precomputed in 0..1 space so the renderer can
 *    skip a per-frame ``overlayBoundingRect`` call.
 */
export interface NormalizedOverlay {
  uuid: string
  document_uuid: string
  overlay_type: OverlayType
  status: OverlayStatus
  page_number: number
  anchor: OverlayAnchor
  payload: Record<string, unknown>
  author_user_id: number | null
  author_kind: OverlayAuthorKind
  revision: number
  document_version: string | null
  created_at: string
  updated_at: string
  /** Cached for performance. ``null`` when kind is ``point``. */
  boundingRect: { x: number; y: number; width: number; height: number } | null
  /** True when the anchor's coordinate schema matches what this client
   * knows how to render. The renderer should short-circuit (or render a
   * "stale" affordance) when false. */
  supportsRender: boolean
}

/** Convert a wire overlay into the normalised shape the store + UI use.
 *
 * Throws on missing-required fields (uuid, anchor) — these would mean a
 * server bug; the store catches and logs rather than crashes the UI. */
export function normalizeOverlay(row: Overlay): NormalizedOverlay {
  if (!row.uuid) throw new Error('overlay row is missing uuid')
  if (!row.anchor) throw new Error('overlay row is missing anchor')
  const anchor = row.anchor
  // Prefer the anchor's page number — the schema enforces they match,
  // but historical rows may have drifted, and the renderer can only
  // honour one source of truth. The anchor wins.
  const page_number = anchor.page
  const payload =
    row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
      ? (row.payload as Record<string, unknown>)
      : {}
  return {
    uuid: row.uuid,
    document_uuid: row.document_uuid,
    overlay_type: row.overlay_type,
    status: row.status,
    page_number,
    anchor,
    payload,
    author_user_id: row.author_user_id ?? null,
    author_kind: row.author_kind,
    revision: row.revision,
    document_version: row.document_version ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    boundingRect: overlayBoundingRect(anchor),
    supportsRender: isOverlayAnchorSupported(anchor),
  }
}

/** Bulk normaliser. Filters out rows that fail single-row normalisation —
 * the store logs them via the optional ``onError`` hook so the UI can
 * keep going instead of crashing on one bad row. */
export function normalizeOverlays(
  rows: readonly Overlay[],
  onError?: (err: Error, row: Overlay) => void,
): NormalizedOverlay[] {
  const out: NormalizedOverlay[] = []
  for (const row of rows) {
    try {
      out.push(normalizeOverlay(row))
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      if (onError) onError(err, row)
      // Otherwise skip silently — the row never makes it into the store.
    }
  }
  return out
}

/** Group normalised overlays by ``page_number``. The renderer iterates
 * pages, not rows, so we precompute the bucket once instead of filtering
 * inside the React component. */
export function groupOverlaysByPage(
  rows: readonly NormalizedOverlay[],
): Map<number, NormalizedOverlay[]> {
  const out = new Map<number, NormalizedOverlay[]>()
  for (const row of rows) {
    const bucket = out.get(row.page_number)
    if (bucket) {
      bucket.push(row)
    } else {
      out.set(row.page_number, [row])
    }
  }
  return out
}
